// 📄 C:\Users\Caner\inosel-panel\ortak\src\fatura-takip\src\pages\KategoriYonetimi.js
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import Select from "react-select";

const SAYFA_BOYUTU = 20;

const KategoriYonetimi = () => {
  const [tumIcerikler, setTumIcerikler] = useState([]); // [{ad, kategori}]
  const [secilenIcerikler, setSecilenIcerikler] = useState([]); // çoklu seçim
  const [grupAdi, setGrupAdi] = useState(null);
  const [altGrup, setAltGrup] = useState(null);

  const [kategoriler, setKategoriler] = useState([]); // [{id,kategori,grup,altGrup,icerik:[]}]
  const [grupSecenekleri, setGrupSecenekleri] = useState([]);
  const [altGrupSecenekleri, setAltGrupSecenekleri] = useState([]);

  const [icerikFiltre, setIcerikFiltre] = useState("");
  const [kategoriFiltre, setKategoriFiltre] = useState(null);

  const [currentPage, setCurrentPage] = useState(1); // sayfalama

  // --- İlk yükleme ---
  useEffect(() => {
    const fetchData = async () => {
      const faturaSnap = await getDocs(collection(db, "faturalar"));
      let urunler = [];
      faturaSnap.forEach((d) => {
        const satirlar = d.data().satirlar || [];
        satirlar.forEach((s) =>
          urunler.push({ ad: (s.urunAdi || "").toString(), kategori: "Sanayi" })
        );
      });
      const tekilUrunler = [
        ...new Map(urunler.map((item) => [item.ad, item])).values(),
      ];

      const issueSnap = await getDocs(collection(db, "issues"));
      const issueList = issueSnap.docs.map((d) => (d.data().issue || "").toString());
      const tekilEksiklikler = [...new Set(issueList)].map((ad) => ({
        ad,
        kategori: "Eksiklik",
      }));

      setTumIcerikler([...tekilUrunler, ...tekilEksiklikler]);
    };

    const fetchKategoriler = async () => {
      const snap = await getDocs(collection(db, "categories"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setKategoriler(list);

      const grupSet = new Set(list.map((k) => k.grup).filter(Boolean));
      const altGrupSet = new Set(list.map((k) => k.altGrup).filter(Boolean));

      setGrupSecenekleri([...grupSet].map((g) => ({ value: g, label: g })));
      setAltGrupSecenekleri([...altGrupSet].map((a) => ({ value: a, label: a })));
    };

    fetchData();
    fetchKategoriler();
  }, []);

  // --- Atanmış içerikleri derive et ---
  const atanmisIcerikler = useMemo(() => {
    return kategoriler.flatMap((kat) =>
      (kat.icerik || []).map((ad) => ({
        ad,
        kategori: kat.kategori,
        grup: kat.grup,
        altGrup: kat.altGrup,
        katId: kat.id,
      }))
    );
  }, [kategoriler]);

  // --- Atanabilir içerikler: tumIcerikler - atanmis icerikler ---
  const getAtanabilirIcerikler = useMemo(() => {
    const atanmislar = new Set(kategoriler.flatMap((k) => k.icerik || []));
    return tumIcerikler.filter((i) => !atanmislar.has(i.ad));
  }, [tumIcerikler, kategoriler]);

  // --- Çoklu seçim için grouped options ---
  const groupedOptions = useMemo(() => {
    const atanabilir = getAtanabilirIcerikler;
    return [
      {
        label: "Eksiklik",
        options: atanabilir
          .filter((i) => i.kategori === "Eksiklik")
          .sort((a, b) => a.ad.localeCompare(b.ad))
          .map((i) => ({ label: i.ad, value: i.ad, kategori: i.kategori })),
      },
      {
        label: "Sanayi",
        options: atanabilir
          .filter((i) => i.kategori === "Sanayi")
          .sort((a, b) => a.ad.localeCompare(b.ad))
          .map((i) => ({ label: i.ad, value: i.ad, kategori: i.kategori })),
      },
    ];
  }, [getAtanabilirIcerikler]);

  // --- Filtreleme (sayfalama öncesi) ---
  const filtrelenmisIcerikler = useMemo(() => {
    const ft = (icerikFiltre || "").toLowerCase();
    return atanmisIcerikler.filter((i) => {
      const icerikUyumlu = (i.ad || "").toLowerCase().includes(ft);
      const kategoriUyumlu = kategoriFiltre
        ? i.kategori === kategoriFiltre.kategori && i.grup === kategoriFiltre.grup
        : true;
      return icerikUyumlu && kategoriUyumlu;
    });
  }, [atanmisIcerikler, icerikFiltre, kategoriFiltre]);

  // --- Sayfalama hesapları ---
  const toplamSayfa = Math.max(1, Math.ceil(filtrelenmisIcerikler.length / SAYFA_BOYUTU));
  const gosterilenIcerikler = useMemo(() => {
    const start = (currentPage - 1) * SAYFA_BOYUTU;
    return filtrelenmisIcerikler.slice(start, start + SAYFA_BOYUTU);
  }, [filtrelenmisIcerikler, currentPage]);

  // Filtreler değişirse sayfayı 1'e çek
  useEffect(() => {
    setCurrentPage(1);
  }, [icerikFiltre, kategoriFiltre]);

  // --- Kategori/Grup Select opsiyonları (filtre için) ---
  const kategoriSecenekleri = useMemo(() => {
    return Array.from(new Set(atanmisIcerikler.map((i) => `${i.kategori}|${i.grup}`))).map((s) => {
      const [kategori, grup] = s.split("|");
      return { value: s, label: `${kategori} / ${grup}`, kategori, grup };
    });
  }, [atanmisIcerikler]);

  // --- Modal kalktı (artık yeni grup/alt grup burada oluşturulmuyor) ---
  // Çoklu seçime odaklandığımız için sade bıraktım.

  // --- Kaydet (çoklu atama) ---
  const handleKaydet = async () => {
    if (!secilenIcerikler?.length || !grupAdi)
      return alert("En az bir içerik ve Grup adı zorunludur.");

    // Seçilen içerikleri kategoriye göre grupla → her kategori için aynı group/altGroup'a eklenecek
    const byKategori = new Map(); // kategori -> [ad1, ad2...]
    secilenIcerikler.forEach((opt) => {
      const kategori = opt.kategori; // "Sanayi" | "Eksiklik"
      if (!byKategori.has(kategori)) byKategori.set(kategori, []);
      byKategori.get(kategori).push(opt.value);
    });

    // Güncelleme planı: mevcut kategoriler içinde aynı kombinasyonu bul, yoksa yeni oluştur
    const yeniKategoriler = [...kategoriler]; // local kopya
    const now = serverTimestamp();

    for (const [kategori, adlar] of byKategori.entries()) {
      const alt = altGrup?.value || ""; // alt grup opsiyonel
      const mevcut = yeniKategoriler.find(
        (k) =>
          k.kategori === kategori &&
          k.grup === grupAdi.value &&
          (k.altGrup || "") === alt
      );

      if (mevcut) {
        // mevcut dokümana ekle (duplikeleri engelle)
        const eklenecek = Array.from(new Set([...(mevcut.icerik || []), ...adlar]));
        if (eklenecek.length !== (mevcut.icerik || []).length) {
          const ref = doc(db, "categories", mevcut.id);
          await updateDoc(ref, {
            icerik: eklenecek,
            updatedAt: now,
            updatedBy: auth.currentUser?.email || "anonim",
          });
          // local state güncelle
          mevcut.icerik = eklenecek;
        }
      } else {
        // yeni doküman oluştur
        const payload = {
          kategori,
          grup: grupAdi.value,
          altGrup: alt,
          icerik: Array.from(new Set(adlar)),
          createdAt: now,
          createdBy: auth.currentUser?.email || "anonim",
        };
        const newRef = await addDoc(collection(db, "categories"), payload);
        yeniKategoriler.push({ id: newRef.id, ...payload });
      }
    }

    setKategoriler(yeniKategoriler); // state'e yaz → atanabilir/atanmış listeler kendini yeniler
    alert("Kategori(ler)e atandı ✅");

    // formu sıfırla (sayfayı yenilemeden)
    setSecilenIcerikler([]);
    setGrupAdi(null);
    setAltGrup(null);
  };

  // --- İçerik kaldır (sayfa yenilemeden) ---
  const handleIcerikBosalt = async (icerikAdi, katId) => {
    const idx = kategoriler.findIndex((k) => k.id === katId);
    if (idx === -1) return;

    const kategori = kategoriler[idx];
    const yeni = (kategori.icerik || []).filter((i) => i !== icerikAdi);

    // Firestore
    await updateDoc(doc(db, "categories", katId), {
      icerik: yeni,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.email || "anonim",
    });

    // local state güncelle
    const kopya = [...kategoriler];
    kopya[idx] = { ...kopya[idx], icerik: yeni };
    setKategoriler(kopya);

    alert("İçerik gruptan kaldırıldı.");
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "auto", padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <style>{`
        label { font-weight: bold; margin-bottom: 4px; display: block; }
        input, .react-select__control { padding: 6px; width: 100%; margin-bottom: 10px; }
        button { cursor: pointer; }
        .button-primary { background-color: #1e3a8a; color: white; padding: 8px 16px; border: none; border-radius: 4px; }
        .button-warning { background-color: #f59e0b; color: white; padding: 4px 10px; border: none; border-radius: 4px; font-size: 12px; }
        .list-item { background: white; border: 1px solid #ccc; border-radius: 6px; padding: 10px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; }
        .pagination { display:flex; gap:8px; align-items:center; justify-content:flex-end; margin-top:8px; }
        .page-btn { background:#e5e7eb; border:none; border-radius:6px; padding:6px 10px; }
        .page-btn[disabled] { opacity:0.5; cursor:not-allowed; }
      `}</style>

      <h1 style={{ fontSize: "24px", marginBottom: "20px", color: "#1e40af" }}>Kategori Yönetimi</h1>

      {/* Üst form: Çoklu seçim + Grup + Alt Grup */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        <div>
          <label>Kategori (Sanayi / Eksiklik) — Çoklu Seçim</label>
          <Select
            options={groupedOptions}
            isSearchable
            isMulti
            closeMenuOnSelect={false}
            placeholder="İçerik ara ve seç..."
            value={secilenIcerikler.map((i) => ({ label: i.value, value: i.value, kategori: i.kategori }))}
            onChange={(multi) => {
              const arr = (multi || []).map((opt) => ({
                value: opt.value,
                kategori: opt.kategori,
              }));
              setSecilenIcerikler(arr);
            }}
          />
        </div>

        <div>
          <label>Grup</label>
          <Select
            options={grupSecenekleri}
            value={grupAdi}
            onChange={(e) => setGrupAdi(e)}
            placeholder="Grup seç"
          />
        </div>

        <div>
          <label>Alt Grup (isteğe bağlı)</label>
          <Select
            options={altGrupSecenekleri}
            value={altGrup}
            onChange={(e) => setAltGrup(e)}
            isClearable
            placeholder="Alt grup seç (opsiyonel)"
          />
        </div>
      </div>

      <button onClick={handleKaydet} className="button-primary">💾 Kaydet</button>

      <hr style={{ margin: "30px 0" }} />

      {/* Filtreler */}
      <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>Atanmış İçerikler</h2>
      <div style={{ display: "flex", gap: "20px", marginBottom: "20px", flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="İçerik ara..."
          value={icerikFiltre}
          onChange={(e) => setIcerikFiltre(e.target.value)}
          style={{ flex: 1, minWidth: "250px" }}
        />
        <div style={{ flex: 1, minWidth: "250px" }}>
          <Select
            options={kategoriSecenekleri}
            isClearable
            placeholder="Kategori / Grup filtresi..."
            value={kategoriFiltre}
            onChange={(e) => setKategoriFiltre(e)}
          />
        </div>
      </div>

      {/* Liste (sayfalı) */}
      <ul style={{ listStyle: "none", padding: 0 }}>
        {gosterilenIcerikler.map((i) => (
          <li key={`${i.katId}::${i.ad}`} className="list-item">
            <span>
              <strong>{i.ad}</strong> → {i.kategori} / {i.grup}
              {i.altGrup ? " / " + i.altGrup : ""}
            </span>
            <button
              onClick={() => handleIcerikBosalt(i.ad, i.katId)}
              className="button-warning"
            >
              🗑️ Gruptan Kaldır
            </button>
          </li>
        ))}
      </ul>

      {/* Sayfalama kontrolleri */}
      <div className="pagination">
        <button
          className="page-btn"
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >
          ◀ Önceki
        </button>
        <span>
          Sayfa {currentPage} / {toplamSayfa} — Toplam {filtrelenmisIcerikler.length} kayıt
        </span>
        <button
          className="page-btn"
          onClick={() => setCurrentPage((p) => Math.min(toplamSayfa, p + 1))}
          disabled={currentPage === toplamSayfa}
        >
          Sonraki ▶
        </button>
      </div>
    </div>
  );
};

export default KategoriYonetimi;
